import React, { useState } from 'react';

import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { ApiOutlined, ReloadOutlined, UploadOutlined } from '@ant-design/icons';

import useDemoMode from 'hooks/useDemoMode';
import useAccountScope from 'hooks/useAccountScope';
import ImportBrokerCSVFlyin from 'components/flyins/ImportBrokerCSVFlyin';
import IbkrFlexConnectionFlyin from 'components/flyins/IbkrFlexConnectionFlyin';
import MainCard from 'components/MainCard';
import MainContainer from 'components/@extended/MainContainer';
import { openSnackbar } from 'api/snackbar';

export default function AccountSettings() {
    const [importFlyinOpen, setImportFlyinOpen] = useState(false);
    const [importDismissed, setImportDismissed] = useState(false);
    const [flexFlyinOpen, setFlexFlyinOpen] = useState(false);
    const { demoMode, setDemoMode, showDemoAccount, setShowDemoAccount, resetDemoAccount } = useDemoMode();
    const { setScope } = useAccountScope();
    const [demoSaving, setDemoSaving] = useState(false);

    return (
        <MainContainer>
            <MainCard sx={{ mt: 2 }}>
                <Typography variant="h4" sx={{ mb: 3 }}>Account Settings</Typography>
                <Grid container spacing={3}>
                    {!importDismissed && (
                        <Grid item xs={12}>
                            <MainCard title="Connect Your Broker" secondary={<ApiOutlined />}>
                                <Stack spacing={1}>
                                    <Typography variant="body2">
                                        Review IBKR live-state health, manage historical Flex sync, or import a CSV
                                        export as a fallback.
                                    </Typography>
                                    <Stack direction="row" spacing={2} flexWrap="wrap">
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<ApiOutlined />}
                                            onClick={() => setFlexFlyinOpen(true)}
                                        >
                                            Manage IBKR
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            startIcon={<UploadOutlined />}
                                            onClick={() => setImportFlyinOpen(true)}
                                        >
                                            Import CSV instead
                                        </Button>
                                        <Button variant="text" onClick={() => setImportDismissed(true)}>Skip</Button>
                                    </Stack>
                                </Stack>
                            </MainCard>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <MainCard title="Demo account">
                            <Stack spacing={1.5}>
                                <Typography variant="body2">
                                    Show the Demo account in the account selector. This preference does not
                                    activate demo data; choose Demo from the account selector when you want
                                    to view the curated sample trades.
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={Boolean(showDemoAccount)}
                                            disabled={demoSaving}
                                            onChange={async (e) => {
                                                const next = e.target.checked;
                                                setDemoSaving(true);
                                                try {
                                                    await setShowDemoAccount(next);
                                                    if (!next && demoMode) {
                                                        setScope({ type: 'all', accountId: null, label: 'All accounts' });
                                                        await setDemoMode(false);
                                                    }
                                                    openSnackbar({
                                                        open: true,
                                                        message: next ? 'Demo is now available in the account selector.' : 'Demo was removed from the account selector.',
                                                        variant: 'alert',
                                                        alert: { color: 'success' }
                                                    });
                                                } catch (err) {
                                                    openSnackbar({
                                                        open: true,
                                                        message: `Failed to update the Demo account preference: ${err?.message || 'unknown error'}`,
                                                        variant: 'alert',
                                                        alert: { color: 'error' }
                                                    });
                                                } finally {
                                                    setDemoSaving(false);
                                                }
                                            }}
                                        />
                                    }
                                    label={showDemoAccount ? 'Show Demo account' : 'Hide Demo account'}
                                />
                                <Button
                                    variant="outlined"
                                    color="warning"
                                    startIcon={<ReloadOutlined />}
                                    disabled={demoSaving}
                                    onClick={async () => {
                                        if (!window.confirm('Reset only your isolated Demo account to the canonical sample dataset? Real and imported accounts will not be changed.')) return;
                                        setDemoSaving(true);
                                        try {
                                            await resetDemoAccount();
                                            setScope({ type: 'demo', accountId: null, label: 'Demo' });
                                            openSnackbar({ open: true, message: 'Demo account restored to its original sample data.', variant: 'alert', alert: { color: 'success' } });
                                        } catch (err) {
                                            openSnackbar({ open: true, message: `Failed to reset Demo: ${err?.message || 'unknown error'}`, variant: 'alert', alert: { color: 'error' } });
                                        } finally {
                                            setDemoSaving(false);
                                        }
                                    }}
                                    sx={{ alignSelf: 'flex-start' }}
                                >
                                    Reset Demo
                                </Button>
                            </Stack>
                        </MainCard>
                    </Grid>
                </Grid>
            </MainCard>
            <ImportBrokerCSVFlyin
                open={importFlyinOpen}
                onClose={() => setImportFlyinOpen(false)}
                onSuccess={() => {
                    setImportDismissed(true);
                }}
            />
            <IbkrFlexConnectionFlyin
                open={flexFlyinOpen}
                onClose={() => setFlexFlyinOpen(false)}
                onConnected={() => setImportDismissed(true)}
                onOpenCsvImport={() => setImportFlyinOpen(true)}
            />
        </MainContainer>
    );
}
